import i18n from '@libs/i18n'

import { IPlan, IPlanPricingPeriod, PlanPeriod } from './types'

export type StandardPlanPeriod = Exclude<PlanPeriod, PlanPeriod.BIMONTHLY>

export interface ISelectableInterval {
  period: PlanPeriod
  price: number
}

const t = i18n.t.bind(i18n)

const getPlanPeriodDiscountObjectDefinition = ({
  selectedPlanPricing,
}: {
  selectedPlanPricing: IPlanPricingPeriod
}) => {
  return {
    [PlanPeriod.MONTHLY]: {
      discountedPrice: selectedPlanPricing.monthly?.amount || 0,
    },
    [PlanPeriod.QUARTERLY]: {
      discountedPrice: selectedPlanPricing.quarterly?.amount || 0,
    },
    [PlanPeriod.HALF_YEARLY]: {
      discountedPrice: selectedPlanPricing['half-yearly']?.amount || 0,
    },
    [PlanPeriod.YEARLY]: {
      discountedPrice: selectedPlanPricing.yearly?.amount || 0,
    },
    [PlanPeriod.BIENNIALLY]: {
      discountedPrice: selectedPlanPricing.biennially?.amount || 0,
    },
  }
}

export const getIntervalLabelFromPlanPeriod = ({
  planPeriod,
  includePrefixModifier,
}: {
  planPeriod: StandardPlanPeriod
  includePrefixModifier: boolean
}) => {
  const messageDataByPeriod = {
    [PlanPeriod.MONTHLY]: {
      prefix: 'per',
      label: 'month',
    },
    [PlanPeriod.QUARTERLY]: {
      prefix: 'per',
      label: '3 months',
    },
    [PlanPeriod.HALF_YEARLY]: {
      prefix: 'per',
      label: '6 months',
    },
    [PlanPeriod.YEARLY]: {
      prefix: 'per',
      label: 'year',
    },
    [PlanPeriod.BIENNIALLY]: {
      prefix: 'per',
      label: '2 years',
    },
  }
  const messageData = messageDataByPeriod[planPeriod]

  const prefix = includePrefixModifier ? `${messageData.prefix} ` : ''
  const label = messageData.label

  return t(`${prefix}${label}`)
}

export const getSubscriptionLabelFromPlanPeriod = ({
  planPeriod,
}: {
  planPeriod: PlanPeriod | undefined
}) => {
  switch (planPeriod) {
    case PlanPeriod.MONTHLY:
      return t('Month')
    case PlanPeriod.HALF_YEARLY:
      return t('6 Months')
    case PlanPeriod.QUARTERLY:
      return t('3 Months')
    case PlanPeriod.YEARLY:
      return t('Year')
    case PlanPeriod.BIENNIALLY:
      return t('2 Years')
    case undefined:
      return '---'
    default:
      return planPeriod[0].toUpperCase() + planPeriod.slice(1)
  }
}

const getFirstOrLastValidPeriodPricingOption = ({
  elementToGetByOrder,
  selectedPlanPricing,
}: {
  elementToGetByOrder: 'first' | 'last'
  selectedPlanPricing: IPlanPricingPeriod
}) => {
  const discountValue = getPlanPeriodDiscountObjectDefinition({
    selectedPlanPricing,
  })

  const periodOrder: Omit<PlanPeriod, PlanPeriod.BIMONTHLY>[] = [
    PlanPeriod.MONTHLY,
    PlanPeriod.QUARTERLY,
    PlanPeriod.HALF_YEARLY,
    PlanPeriod.YEARLY,
    PlanPeriod.BIENNIALLY,
  ]

  const sortedPeriods =
    elementToGetByOrder === 'first' ? periodOrder : [...periodOrder].reverse()

  for (const period of sortedPeriods) {
    if (
      discountValue[period as keyof typeof discountValue].discountedPrice > 0
    ) {
      return period as keyof typeof selectedPlanPricing
    }
  }
}

export const getFirstValidPeriodPricingOption = ({
  selectedPlanPricing,
}: {
  selectedPlanPricing: IPlanPricingPeriod
}) =>
  getFirstOrLastValidPeriodPricingOption({
    selectedPlanPricing,
    elementToGetByOrder: 'first',
  })

export const getLastValidPeriodPricingOption = ({
  selectedPlanPricing,
}: {
  selectedPlanPricing: IPlanPricingPeriod
}) =>
  getFirstOrLastValidPeriodPricingOption({
    selectedPlanPricing,
    elementToGetByOrder: 'last',
  })

const getIntervalPriceData = ({
  selectedPlan,
  elementGetter,
}: {
  selectedPlan: IPlan | undefined
  elementGetter: () => keyof IPlanPricingPeriod | undefined
}) => {
  if (!selectedPlan?.pricingPerPeriod) {
    return undefined
  }

  const periodPricingOptionFunction = elementGetter
  const discountedValue = getPlanPeriodDiscountObjectDefinition({
    selectedPlanPricing: selectedPlan.pricingPerPeriod,
  })

  const periodPricingOption = periodPricingOptionFunction()
  if (!periodPricingOption) {
    return undefined
  }

  return {
    period: periodPricingOption,
    price: discountedValue[periodPricingOption].discountedPrice,
  } as ISelectableInterval
}

export const getBiggestThreePaymentFrequenciesByPeriod = ({
  plan,
}: {
  plan?: IPlan
}): ISelectableInterval[] => {
  if (!plan || !plan.pricingPerPeriod) {
    return []
  }

  const discountedValue = getPlanPeriodDiscountObjectDefinition({
    selectedPlanPricing: plan.pricingPerPeriod,
  })

  const periodOrder: Omit<PlanPeriod, PlanPeriod.BIMONTHLY>[] = [
    PlanPeriod.BIENNIALLY,
    PlanPeriod.YEARLY,
    PlanPeriod.HALF_YEARLY,
    PlanPeriod.QUARTERLY,
    PlanPeriod.MONTHLY,
  ]

  const paymentFrequencies = periodOrder.filter(
    (period) =>
      discountedValue[period as keyof typeof discountedValue].discountedPrice >
      0
  ) as Array<keyof IPlanPricingPeriod>

  return paymentFrequencies.slice(0, 3).map((v) => {
    return {
      period: v,
      price: discountedValue[v].discountedPrice,
    }
  })
}

export const getLowestSelectableInterval = ({
  selectedPlan,
}: {
  selectedPlan: IPlan | undefined
}) =>
  getIntervalPriceData({
    selectedPlan,
    elementGetter: () =>
      getFirstValidPeriodPricingOption({
        selectedPlanPricing: selectedPlan!.pricingPerPeriod,
      }),
  })

export const getHighestSelectableInterval = ({
  selectedPlan,
}: {
  selectedPlan: IPlan | undefined
}) =>
  getIntervalPriceData({
    selectedPlan,
    elementGetter: () =>
      getLastValidPeriodPricingOption({
        selectedPlanPricing: selectedPlan!.pricingPerPeriod,
      }),
  })

export const getPlanDiscountValue = ({
  selectedPlanPricing,
  selectedPeriod,
}: {
  selectedPlanPricing: IPlanPricingPeriod
  selectedPeriod: StandardPlanPeriod
}) => {
  const discountValue = getPlanPeriodDiscountObjectDefinition({
    selectedPlanPricing,
  })

  const firstValidMonthOption = getFirstValidPeriodPricingOption({
    selectedPlanPricing,
  })

  if (!firstValidMonthOption) {
    return 0
  }

  const firstValidMonth = discountValue?.[firstValidMonthOption]
  const { discountedPrice } = discountValue[selectedPeriod]
  const basePrice = firstValidMonth.discountedPrice

  return Math.floor(((basePrice - discountedPrice) / basePrice) * 100)
}

export const getPlanWithBestDeal = ({ plan }: { plan: IPlan }) => {
  const pricingEntries = Object.entries(plan.pricingPerPeriod)
  const lowestPriceEntry = pricingEntries.reduce(
    (currentLowest, currentEntry) =>
      currentLowest[1] > currentEntry[1] ? currentEntry : currentLowest
  )
  return lowestPriceEntry[0]
}
